import React, { useEffect, useState } from 'react';
import SubmitButton from '../../formComponents/SubmitButton';
import ErrorMessage from '../../formComponents/ErrorMessage';

const defaultRules = {
  releaseDayOfWeek: 4,
  bookDirectTagOverrides: [],
  cancelAnyTagOverrides: [],
};

function cleanTagArray(value) {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .filter((tag) => typeof tag === 'string')
        .map((tag) => tag.trim())
        .filter(Boolean)
    )
  );
}

export default function EditBoatBookingRulesForm({
  isOpen,
  onClose,
  onSave,
  initialRules,
  teamTags = [],
  title = 'Boat Booking Rules',
  rulesDefaults = defaultRules,
}) {
  const [rules, setRules] = useState(defaultRules);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const next = { ...defaultRules, ...(rulesDefaults || {}), ...(initialRules || {}) };
    next.bookDirectTagOverrides = cleanTagArray(next.bookDirectTagOverrides);
    next.cancelAnyTagOverrides = cleanTagArray(next.cancelAnyTagOverrides);
    setRules(next);
    setSubmitting(false);
    setError(null);
  }, [isOpen, initialRules, rulesDefaults]);

  if (!isOpen) return null;

  const set = (k, v) => setRules(p => ({ ...p, [k]: v }));
  const toggleTag = (key, tagName) => {
    setRules((prev) => {
      const current = new Set(cleanTagArray(prev[key]));
      if (current.has(tagName)) current.delete(tagName);
      else current.add(tagName);
      return { ...prev, [key]: Array.from(current) };
    });
  };
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSave({
        ...rules,
        bookDirectTagOverrides: cleanTagArray(rules.bookDirectTagOverrides),
        cancelAnyTagOverrides: cleanTagArray(rules.cancelAnyTagOverrides),
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save boat booking rules.');
    } finally {
      setSubmitting(false);
    }
  };
  const selectableTags = Array.from(
    new Set(
      (Array.isArray(teamTags) ? teamTags : [])
        .filter((tag) => tag?.type !== 'ageRule')
        .map((tag) => (typeof tag?.name === 'string' ? tag.name.trim() : ''))
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const directCount = cleanTagArray(rules.bookDirectTagOverrides).length;
  const cancelCount = cleanTagArray(rules.cancelAnyTagOverrides).length;

  return (
    <form onSubmit={submit} className="modal-form">
      <h2>{title}</h2>
      {error && <ErrorMessage message={error} />}

      <section className="form-section boat-request-panel">
        <div className="boat-request-section-head">
          <h3>Allocation</h3>
        </div>
        <div className="boat-rules-grid">
          <label className="form-label">
            Release day of week
            <select
              value={rules.releaseDayOfWeek}
              onChange={e => set('releaseDayOfWeek', Number(e.target.value))}
            >
              <option value={1}>Monday</option>
              <option value={2}>Tuesday</option>
              <option value={3}>Wednesday</option>
              <option value={4}>Thursday</option>
              <option value={5}>Friday</option>
              <option value={6}>Saturday</option>
              <option value={0}>Sunday</option>
            </select>
            <span className="help-text">Main weekly allocation run day.</span>
          </label>
        </div>
      </section>

      {title.toLowerCase().includes('sailing') && (
        <section className="form-section boat-request-panel">
          <div className="boat-request-section-head"><h3>Boat payment card fees</h3></div>
          <label className="form-label">
            Stripe processing fees
            <select value={rules.boatPaymentFeeHandling === 'add' ? 'add' : 'deduct'} onChange={(event) => set('boatPaymentFeeHandling', event.target.value)}>
              <option value="deduct">Deduct fees from the club payout</option>
              <option value="add">Add fees to the member’s checkout total</option>
            </select>
            <span className="help-text">This policy applies to every paid sailing boat.</span>
          </label>
        </section>
      )}

      <section className="form-section boat-request-panel">
        <div className="boat-request-section-head">
          <h3>Tag Overrides</h3>
          <span className="text-muted">
            {directCount} direct-book · {cancelCount} cancel-any
          </span>
        </div>
        {selectableTags.length === 0 ? (
          <div className="text-muted">No tags available. Add team tags first.</div>
        ) : (
          <>
            <div className="boat-rules-group">
              <div className="form-label">
                Allow direct booking outside weekly allocation
              </div>
              <div className="text-muted boat-rules-help">
                Members with these tags can be booked immediately when a boat is free.
              </div>
              <div className="boat-rules-tag-grid">
                {selectableTags.map((tagName) => (
                  <label key={`book:${tagName}`} className="boat-rules-tag-option">
                    <input
                      type="checkbox"
                      checked={Array.isArray(rules.bookDirectTagOverrides) && rules.bookDirectTagOverrides.includes(tagName)}
                      onChange={() => toggleTag('bookDirectTagOverrides', tagName)}
                    />
                    {tagName}
                  </label>
                ))}
              </div>
            </div>

            <div className="boat-rules-group">
              <div className="form-label">
                Allow cancelling any booking/request
              </div>
              <div className="text-muted boat-rules-help">
                Members with these tags can cancel other members&apos; requests too.
              </div>
              <div className="boat-rules-tag-grid">
                {selectableTags.map((tagName) => (
                  <label key={`cancel:${tagName}`} className="boat-rules-tag-option">
                    <input
                      type="checkbox"
                      checked={Array.isArray(rules.cancelAnyTagOverrides) && rules.cancelAnyTagOverrides.includes(tagName)}
                      onChange={() => toggleTag('cancelAnyTagOverrides', tagName)}
                    />
                    {tagName}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
      </section>

      <div className="form-actions">
        <SubmitButton type="button" onClick={onClose} label="Cancel" disabled={submitting} />
        <SubmitButton type="submit" primary label={submitting ? 'Saving…' : 'Save Rules'} disabled={submitting} />
      </div>
    </form>
  );
}
